/**
 * StageAssociationCard.TaskStageDisplay — task-specific stage display.
 *
 * For tasks (ahoy_task), StageAssociationCard renders a prominent,
 * color-coded stage badge instead of a generic DiffSection table. The
 * target stage is the visual hero — its pipeline color tints the badge
 * background, ring, and dot so the action reads instantly: "Mark this task
 * as [In Progress]".
 *
 * The color-coded pill is the canonical PipelineStageValueBadge from
 * lib/crm/components/properties/labels/ — shared with pipeline columns,
 * slideouts, and any other surface that renders stage chips.
 */

import { ArrowRight } from '@untitledui/icons';

import type { StageAssociationValue } from '@/lib/crm/components/properties/inputs/AssociatedPipelineStageInput';
import { AssociatedPipelineStageInput } from '@/lib/crm/components/properties/inputs/AssociatedPipelineStageInput';

import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import { ActionStageBadge } from './ActionStageBadge';

export function TaskStageDisplay({
  showEditInput,
  editStageId,
  handleStageChange,
  entityType,
  associationTypeName,
  proposedValue,
  currentValue,
  orgId,
  enrichedData,
}: {
  showEditInput: boolean;
  editStageId: string | null;
  handleStageChange: (assoc: StageAssociationValue | null) => void;
  entityType: string;
  associationTypeName: string;
  proposedValue: string | null;
  currentValue: string | undefined;
  orgId: number | undefined;
  enrichedData: ActionRendererProps['enrichedData'];
}) {
  if (showEditInput) {
    return (
      <div className='rounded-lg border border-border-secondary bg-bg-secondary p-3 space-y-2'>
        <span className='text-xs font-medium text-fg-tertiary uppercase tracking-wide'>
          Mark this task as
        </span>
        <AssociatedPipelineStageInput
          value={editStageId}
          onChange={handleStageChange}
          entityType={entityType}
          associationTypeName={associationTypeName}
          ariaLabel='Target stage for this task'
        />
      </div>
    );
  }

  // Read-only: prominent stage badge with color from the pipeline
  return (
    <div className='rounded-lg border border-border-secondary bg-bg-secondary p-3'>
      <div className='flex items-center gap-3'>
        <span className='text-sm text-fg-secondary shrink-0'>
          Mark this task as
        </span>
        {currentValue && (
          <>
            <ActionStageBadge
              value={currentValue}
              orgId={orgId}
              entityType={entityType}
              size='sm'
              muted
            />
            <ArrowRight className='w-3.5 h-3.5 text-fg-quaternary shrink-0' />
          </>
        )}
        <ActionStageBadge
          value={proposedValue}
          orgId={orgId}
          entityType={entityType}
          fallbackLabel={enrichedData?.target_object?.label}
        />
      </div>
    </div>
  );
}
